/**
 * Vehicle Inspection Agent for Ameen Insurance Assistant
 *
 * Sub-flow agent for vehicle inspection (policy or claim).
 *
 * Live survey flow: per-step image validation via validateVehicleImage.
 * Upload flow: batch analysis via analyzeVehicleImages (analyze_vehicle tool).
 *
 * TOOLS (generic names, declared in backend):
 * - show_survey_options, start_camera_stream, take_vehicle_picture,
 * - request_close_camera, request_vehicle_images, analyze_vehicle,
 * - return_to_caller, search_faq, go_to_router_agent
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager, FlowStep } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { handleSearchFAQ } from './RouterAgent';
import { getVehicleInspectionReturnAgent } from '../transition-graph';
import { analyzeVehicleImages } from '../../services/geminiService';
import { validateVehicleImage } from '../../services/geminiService';

const VALID_SIDES = ['Chassis', 'Front', 'Right', 'Back', 'Left'] as const;
const SIDE_ORDER = ['Chassis', 'Front', 'Right', 'Back', 'Left'] as const;

/** Arabic labels for each vehicle side, used when building the summary for Ameen. */
const SIDE_LABELS_AR: Record<string, string> = {
  Chassis: 'الشاسيه',
  Front: 'الجهة الأمامية',
  Right: 'الجهة اليمين',
  Back: 'الجهة الخلفية',
  Left: 'الجهة اليسار',
};

/**
 * Descriptions that indicate no real damage was detected (API defaults / model fallbacks).
 * Used to skip a side when building the combined summary and to prevent the model's own
 * generic summary from overwriting validated per-side results.
 */
const DEFAULT_DAMAGE_PHRASES = new Set([
  'السيارة بحالة جيدة',
  'السيارة بحالة جيده',
  'السياره بحالة جيدة',
  'السياره بحالة جيده',
  'no damage description provided',
  'no damage',
  'good condition',
]);

function isDefaultPhrase(text: string): boolean {
  return !text || DEFAULT_DAMAGE_PHRASES.has(text.trim().toLowerCase()) ||
    DEFAULT_DAMAGE_PHRASES.has(text.trim());
}

/**
 * Create Vehicle Inspection Agent node
 */
export function createVehicleInspectionAgent(voiceGender: VoiceGender): AgentNode {
  // Per-side state for live survey (let so they can be cleanly reassigned on each activation)
  let sideDescriptions: Partial<Record<string, string>> = {};
  let sideConfidence: Partial<Record<string, number>> = {};
  let chassisNumber: string | null = null;
  let makeModelMatchResult: string | null = null;

  return {
    id: 'vehicle_inspection_agent',
    name: 'Vehicle Inspection Agent',
    description: 'Sub-flow for vehicle inspection - returns to caller with damage description',

    entryGuardrails: noEntryRequirements(),

    exitGuardrails: {
      transitions: new Map([
        ['return_to_policy', { target: { agentId: 'submit_policy_agent' }, guardrails: [] }],
        ['return_to_claim', { target: { agentId: 'submit_claim_agent' }, guardrails: [] }],
        ['restart_policy', { target: { agentId: 'policy_agent' }, guardrails: [] }],
      ]),
      fallback: { agentId: 'router_agent' },
    },

    initialPrompt: 'ابدأ المعاينة.',

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[VehicleInspectionAgent] Tool called:', toolName, args);

      // Register UIStateManager callbacks on every call so button path always has fresh closures.
      // getVehicleMakeModel: reads vehicleMake/model from context (single source of truth).
      // onButtonCaptureValidated: updates agent closure state from button-path validation results.
      if (uiState) {
        uiState.getVehicleMakeModel = () => {
          const flowState = (context.transitionData?.flow_state ?? {}) as Record<string, any>;
          return {
            make: flowState.vehicleMake as string | undefined,
            model: flowState.vehicleModel as string | undefined,
          };
        };

        uiState.onButtonCaptureValidated = (result) => {
          sideDescriptions[result.side] = result.damageDescription ?? undefined;
          sideConfidence[result.side] = result.confidenceScore;
          if (result.side === 'Chassis') {
            chassisNumber = result.chassisNumber ?? null;
            makeModelMatchResult = result.makeModelMatchResult ?? null;
          }
          logger.info('[VehicleInspectionAgent] Button capture validated:', result.side,
            '| damage:', result.damageDescription?.substring(0, 40));
        };
      }

      switch (toolName) {
        case 'show_survey_options':
          if (uiState) uiState.setFlowStep('vehicle_survey_options');
          return {
            toolName,
            args,
            result: {
              success: true,
              message:
                'Survey options displayed. Waiting for [system_ui] user_selected_live_survey or [system_ui] user_selected_upload_images.',
            },
          };

        case 'start_camera_stream': {
          // Clear the button-path stable ref so fresh survey data is collected.
          // handleStartCameraStream in Altair also clears it, but calling here ensures
          // it's always cleared regardless of which code path triggers the camera.
          uiState?.clearButtonPathDamages?.();

          let success = false;
          if (uiState) {
            success = await uiState.onStartCameraStream();
            if (success) {
              const liveSurveyStep = context.transitionData.caller_agent === 'claim_agent' ? 'claim_vehicle_survey' : 'vehicle_survey';
              uiState.setFlowStep(liveSurveyStep);
            }
          }
          return {
            toolName,
            args,
            result: success
              ? {
                status: 'camera_stream_started',
                message:
                  'Camera stream started. Say the guidance phrase for Chassis and STOP. Do NOT call take_vehicle_picture yet — you MUST wait for the user to say they are ready or tap the capture button before capturing.',
              }
              : { status: 'error', error_code: 'CAMERA_NOT_AVAILABLE', message: 'Unable to start camera stream.' },
          };
        }

        case 'take_vehicle_picture': {
          const side = args?.side;
          if (!side || !VALID_SIDES.includes(side)) {
            return {
              toolName,
              args,
              result: { status: 'error', error_code: 'INVALID_SIDE', message: `side must be one of: ${VALID_SIDES.join(', ')}` },
            };
          }

          const liveSurveyStep = context.transitionData.caller_agent === 'claim_agent' ? 'claim_vehicle_survey' : 'vehicle_survey';
          let imageData: string | null = null;
          if (uiState) imageData = await uiState.onTakeVehiclePicture(side, liveSurveyStep);

          if (!imageData) {
            return {
              toolName,
              args,
              result: { status: 'error', error_code: 'CAPTURE_FAILED', message: 'Unable to capture image. Try again.' },
            };
          }

          // Read vehicleMake/model from UIStateManager (reads context.transitionData.flow_state — single source)
          const { make: vehicleMake, model: vehicleModel } = uiState?.getVehicleMakeModel?.() ?? {};

          let validationResult;
          try {
            validationResult = await validateVehicleImage(imageData, side, vehicleMake, vehicleModel);
          } catch (error: any) {
            logger.error('[VehicleInspectionAgent] Validation network error for', side, ':', error);
            uiState?.removeSurveyImage?.(side);
            return {
              toolName,
              args,
              result: { is_valid: false, message: 'حصل خطأ في التحقق. حاول تاني.', side },
            };
          }

          if (!validationResult.isValid) {
            uiState?.removeSurveyImage?.(side);
            logger.info('[VehicleInspectionAgent]', side, 'image invalid:', validationResult.message);
            return {
              toolName,
              args,
              result: { is_valid: false, message: validationResult.message, side },
            };
          }

          if (side === 'Chassis' && validationResult.makeModelMatchResult === 'mismatch') {
            uiState?.removeSurveyImage?.(side);
            logger.warn('[VehicleInspectionAgent] Chassis make/model mismatch.');
            return {
              toolName,
              args,
              result: {
                is_valid: true,
                make_model_match_result: 'mismatch',
                message: validationResult.message,
                side,
              },
            };
          }

          // Valid — store in agent closure (agent is single source of truth)
          sideDescriptions[side] = validationResult.damageDescription ?? undefined;
          sideConfidence[side] = validationResult.confidenceScore;
          if (side === 'Chassis') {
            chassisNumber = validationResult.chassisNumber ?? null;
            makeModelMatchResult = validationResult.makeModelMatchResult ?? null;
          }

          logger.info('[VehicleInspectionAgent]', side, 'validated. damage:', validationResult.damageDescription?.substring(0, 40));

          return {
            toolName,
            args,
            result: {
              is_valid: true,
              side,
              damage_description: validationResult.damageDescription,
              make_model_match_result: validationResult.makeModelMatchResult ?? null,
            },
          };
        }

        case 'request_close_camera':
          if (uiState) {
            uiState.onCloseCameraStream();
            uiState.setFlowStep('idle');
          }
          return {
            toolName,
            args,
            result: {
              status: 'camera_stream_closed',
              message: 'Camera closed.',
            },
          };

        case 'request_vehicle_images': {
          const callerAgent = context.transitionData.caller_agent;
          const uploadFlowStep = callerAgent === 'claim_agent' ? 'claim_vehicle_upload' : 'vehicle_upload';

          if (uiState) uiState.setFlowStep(uploadFlowStep);
          return {
            toolName,
            args,
            result: {
              success: true,
              message:
                'Upload UI displayed. Waiting for [system_ui] user_uploaded_vehicle_images.',
            },
          };
        }

        case 'analyze_vehicle': {
          // Upload flow only — live survey never calls this tool.
          const res = await handleAnalyzeVehicle(args, toolName, context, uiState);
          return res;
        }

        case 'return_to_caller': {
          // Merge voice-path data (closure) with button-path data (stable ref via UIStateManager).
          // Voice path: updated via take_vehicle_picture tool calls → sideDescriptions closure.
          // Button path: updated via handleSurveyCaptureClick → buttonPathDamagesRef in Altair.
          //   Due to React re-renders that recreate UIStateManager after each image capture,
          //   onButtonCaptureValidated may be stale. buttonPathDamagesRef survives re-renders
          //   and is the reliable source for button-path captures.
          const buttonPathData = uiState?.getButtonPathDamages?.() ?? {};

          // For each side, prefer voice-path data (closure) if available and non-default,
          // otherwise fall back to button-path data.
          const mergedDescriptions: Partial<Record<string, string | null>> = {};
          for (const s of SIDE_ORDER) {
            const closureDesc = sideDescriptions[s]?.trim() || null;
            const buttonDesc = (buttonPathData[s] ?? '').trim() || null;
            // Voice path takes precedence; fall back to button path
            mergedDescriptions[s] = !isDefaultPhrase(closureDesc ?? '') ? closureDesc
              : !isDefaultPhrase(buttonDesc ?? '') ? buttonDesc
                : null;
          }

          logger.info('[VehicleInspectionAgent] Merged side data (voice+button):', mergedDescriptions);

          // Build labelled summary: only include sides that have real damage.
          const damagedSides = SIDE_ORDER.filter(s => {
            const desc = mergedDescriptions[s];
            return desc && !isDefaultPhrase(desc);
          });

          let finalDescription: string;
          if (damagedSides.length > 0) {
            // e.g. "الجهة الأمامية: خدش على الباب الأيمن | الجهة الخلفية: كسر في المصد"
            finalDescription = damagedSides
              .map(s => `${SIDE_LABELS_AR[s]}: ${mergedDescriptions[s]!.trim()}`)
              .join(' | ');
          } else {
            finalDescription = 'السيارة بحالة جيدة';
          }

          logger.info('[VehicleInspectionAgent] Combined damage summary:', finalDescription.substring(0, 100));
          return handleReturnToCaller(args, toolName, context, finalDescription, chassisNumber, makeModelMatchResult);
        }

        case 'go_to_router_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to main menu' },
            triggersTransition: true,
            transitionTo: 'router_agent',
            transitionData: { return_to_menu: true },
          };

        case 'search_faq':
          return await handleSearchFAQ(args, toolName);

        default:
          logger.warn('[VehicleInspectionAgent] Unknown tool:', toolName);
          return { toolName, args, result: { success: false, error: `Unknown tool: ${toolName}` } };
      }
    },

    onActivate: async (context: AgentContext) => {
      logger.info('[VehicleInspectionAgent] Activated');
      // Reset per-session live survey state (let variables reassigned cleanly)
      sideDescriptions = {};
      sideConfidence = {};
      chassisNumber = null;
      makeModelMatchResult = null;
      const callerAgent = context.transitionData.caller_agent;
      logger.info('[VehicleInspectionAgent] Caller agent:', callerAgent);
    },

    onDeactivate: async (context: AgentContext) => {
      logger.info('[VehicleInspectionAgent] Deactivated');
    },
  };
}

const MISMATCH_MESSAGE =
  'للأسف، رقم الشاسيه اللي في صور المعاينة مش بيتوافق مع بيانات العربية اللي اتسجلت في الوثيقة. هنضطر نرجع لبداية إنشاء الوثيقة علشان تتأكد من البيانات الصح.';

/**
 * Handle analyze_vehicle tool — UPLOAD FLOW ONLY.
 * Gets images from UI state, calls batch analysis API, returns damage description.
 * Extracts chassis number and verifies make/model (policy flow only).
 * On make/model mismatch: transitions back to policy_agent.
 */
async function handleAnalyzeVehicle(
  args: Record<string, any>,
  toolName: string,
  context: AgentContext,
  uiState?: UIStateManager
): Promise<ToolCallResult> {
  logger.info('[VehicleInspectionAgent] Analyzing vehicle images (upload flow)');

  try {
    const callerAgent = context.transitionData.caller_agent;
    let images: any[] = [];
    if (uiState) {
      images = callerAgent === 'claim_agent' ? uiState.getClaimSurveyImages() : uiState.getSurveyImages();
    }

    logger.info('[VehicleInspectionAgent] Found', images.length, 'images for analysis');

    if (!images.length) {
      return {
        toolName,
        args,
        result: {
          success: false,
          error: 'No vehicle images available for analysis. Please capture or upload vehicle images first.',
        },
      };
    }

    const imagesWithData = images.filter((img: any) => img?.data?.trim?.());
    if (!imagesWithData.length) {
      return {
        toolName,
        args,
        result: { success: false, error: 'Vehicle images are empty. Please capture or upload again.' },
      };
    }

    const flowState = (context.transitionData.flow_state ?? {}) as Record<string, any>;
    const vehicleMake: string | undefined = flowState.vehicleMake;
    const vehicleModel: string | undefined = flowState.vehicleModel;

    try {
      uiState?.showLoading?.('جاري تحليل صور السيارة...', 'vehicle_analyze');
      const { vehicleAnalyzeDescription, chassisNumber: batchChassisNumber, makeModelMatchResult: batchMatchResult } =
        await analyzeVehicleImages(imagesWithData, vehicleMake, vehicleModel);

      const description = vehicleAnalyzeDescription || 'السيارة بحالة جيده';

      logger.info('[VehicleInspectionAgent] Batch analysis — chassisNumber:', batchChassisNumber, '| matchResult:', batchMatchResult);

      if (batchMatchResult === 'mismatch') {
        logger.warn('[VehicleInspectionAgent] Make/model mismatch (upload flow). Restarting policy flow.');
        return {
          toolName,
          args,
          result: {
            success: false,
            make_model_match_result: 'mismatch',
            chassis_number: batchChassisNumber ?? null,
            message: MISMATCH_MESSAGE,
          },
          triggersTransition: true,
          transitionTo: 'policy_agent',
          transitionDelayMs: -1,
        };
      }

      return {
        toolName,
        args,
        result: {
          success: true,
          imagesCount: imagesWithData.length,
          damage_description: description,
          chassis_number: batchChassisNumber ?? null,
          make_model_match_result: batchMatchResult ?? 'skipped',
          message: 'Analysis complete. Damage description and chassis number included above.',
        },
      };
    } finally {
      uiState?.hideLoading?.();
    }
  } catch (error: any) {
    logger.error('[VehicleInspectionAgent] Error analyzing vehicle:', error);
    uiState?.hideLoading?.();
    return {
      toolName,
      args,
      result: { success: false, error: error.message || 'Failed to analyze vehicle images' },
    };
  }
}

/**
 * Handle return_to_caller tool.
 * Forwards damage_description, chassis_number, and make_model_match_result to the next agent.
 */
function handleReturnToCaller(
  args: Record<string, any>,
  toolName: string,
  context: AgentContext,
  storedDamageDescription: string,
  storedChassisNumber: string | null,
  storedMakeModelMatchResult: string | null
): ToolCallResult {
  const callerAgent = args.caller_agent || context.transitionData.caller_agent;

  // storedDamageDescription is the validated, per-side API result — always prefer it.
  // args.damage_description is the model's own summary which may be a generic default.
  // Only fall back to args.damage_description when stored data is itself a default phrase.
  const storedIsReal = storedDamageDescription && !isDefaultPhrase(storedDamageDescription);
  const argsIsReal = args.damage_description && !isDefaultPhrase(args.damage_description);
  const damageDesc =
    (storedIsReal ? storedDamageDescription : null) ||
    (argsIsReal ? args.damage_description : null) ||
    storedDamageDescription ||
    'السيارة بحالة جيدة';

  logger.info('[VehicleInspectionAgent] Returning to caller:', callerAgent, 'with damage:', damageDesc.substring(0, 50) + '...');

  if (!callerAgent) {
    const returnAgent = getVehicleInspectionReturnAgent(undefined);
    logger.warn('[VehicleInspectionAgent] No caller agent specified, using default:', returnAgent);

    return {
      toolName,
      args,
      result: {
        success: true,
        damage_description: damageDesc,
        message: `نتيجة المعاينة: ${damageDesc}`,
      },
      triggersTransition: true,
      transitionTo: returnAgent,
      transitionData: {
        damage_description: damageDesc,
        chassis_number: storedChassisNumber,
        make_model_match_result: storedMakeModelMatchResult,
        from_inspection: true
      },
      transitionDelayMs: -1
    };
  }

  const validCallers = ['policy_agent', 'claim_agent'];
  if (!validCallers.includes(callerAgent)) {
    logger.warn('[VehicleInspectionAgent] Invalid caller agent:', callerAgent);
    return {
      toolName,
      args,
      result: {
        success: false,
        error: `Invalid caller_agent: ${callerAgent}. Must be policy_agent or claim_agent.`
      }
    };
  }

  const targetAgent = callerAgent === 'claim_agent' ? 'submit_claim_agent' : 'submit_policy_agent';

  logger.info('[VehicleInspectionAgent] Will transition to', targetAgent, 'after audio completion');

  return {
    toolName,
    args,
    result: {
      success: true,
      damage_description: damageDesc,
      message: `نتيجة المعاينة: ${damageDesc}`,
    },
    triggersTransition: true,
    transitionTo: targetAgent,
    transitionData: {
      caller_agent: callerAgent,
      damage_description: damageDesc,
      chassis_number: storedChassisNumber,
      make_model_match_result: storedMakeModelMatchResult,
      from_inspection: true,
      flow_state: context.transitionData.flow_state
    },
    transitionDelayMs: -1
  };
}
